import { Component, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import type { HeaderDropdownConfig, RailBarRouteConfig } from '@dtis/angular';
import { AzureAuthService, DtisAzureRoles, DtisShellComponent, DtisSplashscreenComponent, DtisTranslateService } from '@dtis/angular';
import { environment } from '../environments/environment';
import { UltrasonicStoreService } from './store/ultrasonic-store.service';

@Component({
  imports: [RouterModule, DtisShellComponent, DtisSplashscreenComponent],
  selector: 'dtis-ut-root',
  template: `
    <dtis-splashscreen [maxDurationMs]="durationSplashScreen" />
    <dtis-shell
      [railConfig]="railBarConfig"
      [header_links]="headerLinks"
      [headerDropdown]="equipmentDropdown()"
      [headerReadonlyLabel]="plantReadonlyLabel()"
      [title]="'Ultrasonic Testing'"
      [appVersion]="appVersion"
    ></dtis-shell>
  `,
})
export class AppComponent {
  private readonly store = inject(UltrasonicStoreService);
  private readonly azure = inject(AzureAuthService);
  private readonly i18n = inject(DtisTranslateService);

  readonly durationSplashScreen = environment.e2e ? 0 : 3500;
  /**
   * for displaying in the footer
   */
  appVersion = environment.version;
  /**
   * currently selected equipment ID shown in the header dropdown. It is initialized to the first equipment of the selected plant, if available.
   */
  readonly selectedEquipment = signal(this.store.equipment().find((e) => e.plantId === this.store.selectedPlantId())?.id ?? '');
  /**
   * configuration for the equipment dropdown in the header. It filters the equipment based on the selected plant and ensures that the current selection is valid. If the current selection is no longer valid, it resets to the first available option.
   */
  readonly equipmentDropdown = computed<HeaderDropdownConfig>(() => {
    const plantId = this.store.selectedPlantId();
    const options = this.store
      .equipment()
      .filter((e) => e.plantId === plantId)
      .map((e) => ({ value: e.id, label: e.name }));

    // Ensure the current selection is still valid; reset if not
    if (options.length > 0 && !options.some((o) => o.value === this.selectedEquipment())) {
      this.selectedEquipment.set(options[0].value);
    }

    return {
      label: 'Test Equipment',
      options,
      selected: this.selectedEquipment(),
      onChange: (value: string) => this.selectedEquipment.set(value),
    };
  });
  /**
   *
   */
  readonly plantReadonlyLabel = computed<string | undefined>(() => {
    const plantId = this.store.selectedPlantId();
    const plant = this.store.plants().find((p) => p.id === plantId);
    const plantStr = plant ? `${plant.name} (${plant.abbreviation})` : this.i18n.translate('no-plant-selected');
    const userLevelStr = this.azure.handleAccount()?.idTokenClaims?.roles?.join(', ') ?? 'No roles';

    return `${plantStr} - UserLevel: ${userLevelStr}`;
  });
  /**
   *
   */
  private readonly isAdmin = this.azure.hasRole(DtisAzureRoles.SUPER_USER);
  /**
   *
   */
  readonly railBarConfig: RailBarRouteConfig = {
    isHidden: false,
    routes: [
      {
        name: 'dashboard',
        icon: 'dashboard',
        route: ['/dashboard'],
        exactMatch: true,
      },
      {
        name: 'Order',
        icon: 'description',
        route: ['/orders'],
        disabled: !this.isAdmin,
      },
      {
        name: 'Approval',
        icon: 'check',
        route: ['/approval'],
        disabled: !this.isAdmin,
      },
      {
        name: 'Reports',
        icon: 'summarize',
        route: ['/reports'],
      },
      {
        name: 'Analysis',
        icon: 'analytics',
        route: ['/analysis'],
      },
      {
        name: 'Admin',
        icon: 'admin_panel_settings',
        route: ['/administration'],
        disabled: !this.isAdmin,
      },
    ],
  };

  readonly headerLinks: never[] = [];
}
